/**
 * Mapping between the "minute of the first goal" dial (a single ordinal index)
 * and the backend representation (`firstGoalType` + `matchMinuteOfFirstGoal`).
 *
 * Dial ordinal table (93 states, 0..92):
 *
 * | Dial value | Meaning                    | Backend                                  |
 * | :--------- | :------------------------- | :--------------------------------------- |
 * | 0          | Kein Tor                   | NO_GOAL                                  |
 * | 1–45       | Minute 1–45                | IN_REGULAR_TIME, minute = dial           |
 * | 46         | 45'+ (1st half stoppage)   | ADDITIONAL_TIME_1ST_HALF                 |
 * | 47–91      | Minute 46–90               | IN_REGULAR_TIME, minute = dial − 1       |
 * | 92         | 90'+ (2nd half stoppage)   | ADDITIONAL_TIME_2ND_HALF                 |
 */

import { DisplayText, displayKey, displayText } from '@kikkz/shared/i18n/util';

export type FirstGoalType = 'NO_GOAL' | 'IN_REGULAR_TIME' | 'ADDITIONAL_TIME_1ST_HALF' | 'ADDITIONAL_TIME_2ND_HALF';

/** Highest dial ordinal (92 → "90'+"); the dial encodes 93 states, 0..92. */
export const FIRST_GOAL_DIAL_MAX = 92;

/**
 * Converts the backend representation to the dial ordinal.
 *
 * Returns `null` when `type` is missing, or when `type` is `IN_REGULAR_TIME`
 * and `minute` is missing or outside 1–90.
 */
export function firstGoalToDialValue(
  type: FirstGoalType | null | undefined,
  minute: number | null | undefined,
): number | null {
  switch (type) {
    case 'NO_GOAL':
      return 0;
    case 'ADDITIONAL_TIME_1ST_HALF':
      return 46;
    case 'ADDITIONAL_TIME_2ND_HALF':
      return FIRST_GOAL_DIAL_MAX;
    case 'IN_REGULAR_TIME':
      if (minute == null || minute < 1 || minute > 90) return null;
      return minute <= 45 ? minute : minute + 1;
    default:
      return null;
  }
}

/** Converts a dial ordinal (0–92) to the backend representation. */
export function dialValueToFirstGoal(dial: number): { type: FirstGoalType; minute?: number } {
  if (dial === 0) return { type: 'NO_GOAL' };
  if (dial === 46) return { type: 'ADDITIONAL_TIME_1ST_HALF' };
  if (dial === FIRST_GOAL_DIAL_MAX) return { type: 'ADDITIONAL_TIME_2ND_HALF' };
  return { type: 'IN_REGULAR_TIME', minute: dial <= 45 ? dial : dial - 1 };
}

/**
 * Fallback for legacy backend data without `firstGoalType`, using the OLD
 * encoding of `matchMinuteOfFirstGoal`: 0 = no goal, 1–90 = minute,
 * 91 = 2nd half stoppage time. Returns `null` for `null`/`undefined`.
 */
export function legacyMinuteToFirstGoal(
  minute: number | null | undefined,
): { type: FirstGoalType; minute?: number } | null {
  if (minute == null) return null;
  if (minute === 0) return { type: 'NO_GOAL' };
  if (minute === 91) return { type: 'ADDITIONAL_TIME_2ND_HALF' };
  return { type: 'IN_REGULAR_TIME', minute };
}

/**
 * Formats a dial ordinal (see table above) for display.
 * NOT for real match minutes — use `formatMatchMinute` for those.
 */
export function formatFirstGoalDial(dial: number | null | undefined): DisplayText {
  if (dial == null) return displayText('-');
  if (dial === 0) return displayKey('pg.common.noGoal');
  if (dial === 46) return displayText("45'+");
  if (dial === FIRST_GOAL_DIAL_MAX) return displayText("90'+");
  return displayText(dial <= 45 ? `${dial}'` : `${dial - 1}'`);
}
